import React, { useState, useEffect } from 'react';
import { 
  Users, Search, UserPlus, Mail, 
  CheckCircle2, Clock, ShieldCheck, Trash2, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/admin/Sidebar';
import Header from '../../components/admin/Header';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../utils/api';
import { formatDate, ROLES } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';
import './UsersPage.css';
import './DirectoryModals.css';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'active', 'pending'
  const [selectedRole, setSelectedRole] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: '', teamLevel: '', brand: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', teamLevel: '', brand: '', isActive: true });
  const [editLoading, setEditLoading] = useState(false);
  const { isAdmin } = useAuth();

  const roleColors = {
    superadmin: '#0f172a', admin: '#e11d48', rd: '#7c3aed', 
    regulatory: '#0891b2', qa: '#f59e0b', coo: '#10b981', 
    marketing: '#db2777', cco: '#4f46e5'
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      const filteredUsers = res.data.filter(u => u.role !== 'superadmin');
      setUsers(filteredUsers);
    } catch (err) {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchesSearch =
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q);

    if (!matchesSearch) return false;
    if (selectedRole && u.role !== selectedRole) return false;
    if (activeTab === 'active') return u.isActive && u.isActivated;
    if (activeTab === 'pending') return !u.isActivated;
    return true;
  });

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.role) return toast.error('Please select a role');
    setInviteLoading(true);
    try {
      await api.post('/auth/invite', inviteForm);
      toast.success('Invitation sent successfully!');
      setShowInviteModal(false);
      setInviteForm({ name: '', email: '', role: '', teamLevel: '', brand: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const openEditModal = (user) => {
    if (!isAdmin) return;
    setEditingUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || '',
      teamLevel: user.teamLevel || '',
      brand: user.brand || '',
      isActive: user.isActive
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.role) return toast.error('Please select a role');
    if (['rd', 'regulatory', 'qa'].includes(editForm.role) && !editForm.teamLevel) {
      return toast.error('Please select team member or head of team');
    }
    if (editForm.role === 'marketing' && !editForm.brand) {
      return toast.error('Please select a Marketing brand');
    }

    setEditLoading(true);
    try {
      await api.patch(`/users/${editingUser._id}`, editForm);
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleResendInvite = async (user, e) => {
    e.stopPropagation();
    if (!isAdmin) return;
    if (user.isActivated) return toast.error('This account has already been activated');
    if (!window.confirm(`Resend the activation email to ${user.name || user.email}?`)) return;

    try {
      await api.post(`/users/${user._id}/resend-invite`);
      toast.success('Invitation email resent successfully');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend invite');
    }
  };

  const handleDeleteUser = async (user, e) => {
    e.stopPropagation();
    if (!isAdmin) return;
    setDeleteConfirmUser(user);
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirmUser) return;

    setDeletingUser(true);
    try {
      await api.delete(`/users/${deleteConfirmUser._id}`);
      toast.success('User account deleted successfully');
      setDeleteConfirmUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  return (
    <div className="page-layout users-page">
      <Sidebar />
      <div className="main-content">
        <Header 
          title="Team Management" 
          subtitle="Directory of internal staff and their access levels" 
          actions={
            isAdmin && (
              <button className="btn btn-primary users-invite-button" onClick={() => setShowInviteModal(true)}>
                <UserPlus size={18} />
                <span>Invite Member</span>
              </button>
            )
          }
        />

        <div className="page-body users-page-body">
          
          {/* Quick Stats Grid */}
          <div className="users-role-grid">
            {Object.entries(ROLES).map(([role, info]) => {
              const count = users.filter(u => u.role === role).length;
              if (count === 0) return null;
              return (
                <button
                  key={role}
                  type="button"
                  className={`users-role-stat ${selectedRole === role ? 'active' : ''}`}
                  onClick={() => setSelectedRole(current => current === role ? '' : role)}
                  aria-pressed={selectedRole === role}
                  aria-label={`Filter users by ${info.label}, ${count} total`}
                >
                  <div className="users-role-icon" style={{ backgroundColor: `${roleColors[role]}15`, color: roleColors[role] }}>
                    <ShieldCheck size={20} />
                  </div>
                  <div className="users-role-copy">
                    <div className="users-role-label">{info.label}</div>
                    <div className="users-role-count">{count}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Table Toolbar */}
          <div className="card users-toolbar">
            <div className="users-toolbar-content">
              <div className="users-tab-list" role="group" aria-label="Filter users by status">
                {['all', 'active', 'pending'].map(tab => (
                  <button 
                    type="button"
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`users-tab ${activeTab === tab ? 'active' : ''}`}
                    aria-pressed={activeTab === tab}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div className="users-search">
                <Search size={16} className="users-search-icon" />
                <input 
                  className="form-input users-search-input"
                  placeholder="Filter by name or role..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Table Container */}
          <div className="card users-table-shell">
            {loading ? (
              <div className="users-loading">
                <div className="spinner" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="users-empty">
                <Users size={42} />
                <h3>No members found</h3>
                <p>Try adjusting your search or filters.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="modern-table users-table">
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Team Member</th>
                      <th>Role & Dept</th>
                      <th>Access Status</th>
                      <th>Joined Date</th>
                      {isAdmin && <th>Invitation</th>}
                      {isAdmin && <th>Account</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u, index) => (
                      <tr
                        key={u._id}
                        onClick={() => openEditModal(u)}
                        title={isAdmin ? 'Click to edit user' : undefined}
                        className={`users-row ${index % 2 === 0 ? 'is-odd' : 'is-even'}`}
                      >
                        <td className="users-serial">{index + 1}</td>
                        <td>
                          <div className="users-identity">
                            <div className="users-avatar" style={{ '--user-role-color': roleColors[u.role] || '#64748b' }}>
                              {u.name?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div className="users-identity-copy">
                              <div className="users-name">{u.name || 'Unnamed user'}</div>
                              <div className="users-email">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="users-role-tags">
                            <span style={{ 
                              padding: '2px 10px', borderRadius: '100px', fontSize: '12px', fontWeight: 600,
                              backgroundColor: `${roleColors[u.role]}10`, color: roleColors[u.role], border: `1px solid ${roleColors[u.role]}20`
                            }}>
                              {ROLES[u.role]?.label || u.role}
                            </span>
                            {u.brand && (
                              <span style={{ padding: '2px 10px', borderRadius: '100px', fontSize: '12px', fontWeight: 600, backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #dbeafe' }}>
                                {u.brand}
                              </span>
                            )}
                          </div>
                          {u.teamLevel && (
                            <div className="users-team-level">
                              {u.teamLevel === 'head' ? '★ Department Head' : 'Team Associate'}
                            </div>
                          )}
                        </td>
                        <td>
                          {!u.isActivated ? (
                            <div className="users-access-status is-pending">
                              <Clock size={14} /> Pending account activation
                            </div>
                          ) : (
                            <div className={`users-access-status ${u.isActive ? 'is-active' : 'is-disabled'}`}>
                              <CheckCircle2 size={14} /> {u.isActive ? 'Active' : 'Disabled'}
                            </div>
                          )}
                        </td>
                        <td className="users-date">{formatDate(u.createdAt)}</td>
                        {isAdmin && (
                          <td>
                            {!u.isActivated ? (
                              <button
                                type="button"
                                onClick={(e) => handleResendInvite(u, e)}
                                className="users-action users-action-invite"
                                title="Resend invitation"
                              >
                                <Mail size={14} /> Resend
                              </button>
                            ) : (
                              <span className="users-activated">Activated</span>
                            )}
                          </td>
                        )}
                        {isAdmin && (
                          <td>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteUser(u, e)}
                              className="users-action users-action-delete"
                              title="Delete user account"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modern Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay directory-modal-overlay">
          <div className="modal directory-modal">
            <div className="directory-modal-header">
              <div className="directory-modal-heading">
                <span className="directory-modal-icon"><UserPlus size={20} /></span>
                <div>
                  <span className="directory-modal-eyebrow">Team access</span>
                  <h3>Add Team Member</h3>
                  <p>They will receive an email to set up their password.</p>
                </div>
              </div>
              <button type="button" className="directory-modal-close" onClick={() => setShowInviteModal(false)} aria-label="Close dialog"><X size={18} /></button>
            </div>

            <form onSubmit={handleInvite} className="directory-modal-form">
              <div className="modal-body directory-modal-body">
                <div className="directory-modal-grid">
                  <div className="form-group directory-modal-form-group">
                    <label className="form-label">Full Name</label>
                    <input className="form-input" placeholder="John Doe" value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group directory-modal-form-group">
                    <label className="form-label">Email Address</label>
                    <input className="form-input" type="email" placeholder="john@company.com" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} required />
                  </div>
                </div>

                <div className="form-group directory-modal-form-group">
                  <label className="form-label directory-modal-section-label">Role Selection</label>
                  <div className="directory-role-grid">
                    {Object.entries(ROLES).map(([val, info]) => (
                      <button
                        type="button"
                        key={val}
                        className={`directory-role-choice ${inviteForm.role === val ? 'active' : ''}`}
                        style={{ '--role-color': roleColors[val] }}
                        onClick={() => setInviteForm({ ...inviteForm, role: val, teamLevel: '', brand: '' })}
                        aria-pressed={inviteForm.role === val}
                      >
                        <span className="directory-role-dot" />
                        <span>{info.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {['rd', 'regulatory', 'qa'].includes(inviteForm.role) && (
                  <div className="form-group directory-modal-context">
                    <label className="form-label">Team Seniority</label>
                    <select className="form-input" value={inviteForm.teamLevel} onChange={e => setInviteForm({ ...inviteForm, teamLevel: e.target.value })} required>
                      <option value="">Select level...</option>
                      <option value="member">Team Member</option>
                      <option value="head">Head of Team</option>
                    </select>
                  </div>
                )}

                {inviteForm.role === 'marketing' && (
                  <div className="form-group directory-modal-context">
                    <label className="form-label">Assigned Brand</label>
                    <select className="form-input" value={inviteForm.brand} onChange={e => setInviteForm({ ...inviteForm, brand: e.target.value })} required>
                      <option value="">Select brand...</option>
                      <option value="Hollandia">Hollandia</option>
                      <option value="Chivita">Chivita</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="modal-footer directory-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={inviteLoading}>
                  {inviteLoading ? <div className="spinner" style={{ width: '18px', height: '18px' }} /> : <><Mail size={18} /> Send Invite</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="modal-overlay directory-modal-overlay">
          <div className="modal directory-modal">
            <div className="directory-modal-header">
              <div className="directory-modal-heading">
                <span className="directory-modal-icon"><Users size={20} /></span>
                <div>
                  <span className="directory-modal-eyebrow">Team access</span>
                  <h3>Edit Team Member</h3>
                  <p>Update account details, role metadata, and access status.</p>
                </div>
              </div>
              <button type="button" className="directory-modal-close" onClick={() => setEditingUser(null)} aria-label="Close dialog"><X size={18} /></button>
            </div>

            <form onSubmit={handleEditSubmit} className="directory-modal-form">
              <div className="modal-body directory-modal-body">
                <div className="directory-modal-grid">
                  <div className="form-group directory-modal-form-group">
                    <label className="form-label">Full Name</label>
                    <input className="form-input" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group directory-modal-form-group">
                    <label className="form-label">Email Address</label>
                    <input className="form-input" type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} required />
                  </div>
                </div>

                <div className="form-group directory-modal-form-group">
                  <label className="form-label directory-modal-section-label">Role Selection</label>
                  <div className="directory-role-grid">
                    {Object.entries(ROLES).map(([val, info]) => (
                      <button
                        type="button"
                        key={val}
                        className={`directory-role-choice ${editForm.role === val ? 'active' : ''}`}
                        style={{ '--role-color': roleColors[val] }}
                        onClick={() => setEditForm({ ...editForm, role: val, teamLevel: '', brand: '' })}
                        aria-pressed={editForm.role === val}
                      >
                        <span className="directory-role-dot" />
                        <span>{info.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {['rd', 'regulatory', 'qa'].includes(editForm.role) && (
                  <div className="form-group directory-modal-context">
                    <label className="form-label">Team Seniority</label>
                    <select className="form-input" value={editForm.teamLevel} onChange={e => setEditForm({ ...editForm, teamLevel: e.target.value })} required>
                      <option value="">Select level...</option>
                      <option value="member">Team Member</option>
                      <option value="head">Head of Team</option>
                    </select>
                  </div>
                )}

                {editForm.role === 'marketing' && (
                  <div className="form-group directory-modal-context">
                    <label className="form-label">Assigned Brand</label>
                    <select className="form-input" value={editForm.brand} onChange={e => setEditForm({ ...editForm, brand: e.target.value })} required>
                      <option value="">Select brand...</option>
                      <option value="Hollandia">Hollandia</option>
                      <option value="Chivita">Chivita</option>
                    </select>
                  </div>
                )}

                <label className="directory-modal-checkbox">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })}
                  />
                  Active account
                </label>
              </div>

              <div className="modal-footer directory-modal-footer">
                <button
                  type="button"
                  className={`btn ${editForm.isActive ? 'btn-warning' : 'btn-secondary'} directory-modal-secondary-action`}
                  onClick={() => setEditForm({ ...editForm, isActive: !editForm.isActive })}
                  disabled={editLoading}
                >
                  {editForm.isActive ? 'Deactivate User' : 'Reactivate User'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setEditingUser(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>
                  {editLoading ? <div className="spinner" style={{ width: '18px', height: '18px' }} /> : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteConfirmUser}
        title="Delete user?"
        message={`Delete ${deleteConfirmUser?.name || deleteConfirmUser?.email} permanently from the database?`}
        onConfirm={confirmDeleteUser}
        onCancel={() => setDeleteConfirmUser(null)}
        isDangerous={true}
        isLoading={deletingUser}
      />
    </div>
  );
}
